import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';
import moment from 'moment';

// Redux
import { useSelector } from 'react-redux';

import { Button } from 'react-bootstrap';
import {
  doubleSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';
import ClickableContainer from 'components/clickable-container';
import NvIcon from 'shared/components/nv-icon';
import { genAIGradientType4, gray5 } from 'styles/global_defaults/colors';
import { config } from '../../../../../config/pendo.config.json';
import AiConversationContext,
{ AIRolePlayStatus } from './context';
import ConversationModalHeader from './header';
import ScenarioDetails from './scenario-details';
import ChatBar from './chat-bar';
import useAiRolePlayHelper from '../hooks/use-ai-role-play-helper';
import CompleteStatus from './complete-status';
import AvatarContainer from './avatar-container';
import Chats from './chats';
import AiFeedback from './ai-feedback';
import { ClosingReason } from './stream-ai-response';

const ConversationModalContent = () => {
  const { getResponsivePadding, isTabletOrSmaller } = useAiRolePlayHelper();
  const {
    aiRolePlayStatus,
    showScenarioDetails,
    setShowScenarioDetails,
    messages,
    sessionData,
    isRolePlayStarting,
    handleStartConversation,
  } = useContext(AiConversationContext);
  const submission = useSelector((state) => (sessionData.submissionId ? state.models.aiRolePlaySubmissions[sessionData.submissionId] : null));

  const isBodyPartitioned = !isTabletOrSmaller
    && aiRolePlayStatus === AIRolePlayStatus.COMPLETED
    && submission?.closingReason === ClosingReason.COMPLETED;

  const styles = css`
    border-top: ${quarterSpacing}px solid transparent;
    border-image-source: ${genAIGradientType4};
    border-image-slice: 1;
    .body-container {
      min-height: 0;
      flex-direction: ${isBodyPartitioned ? 'row' : 'column'};
      overflow-y: ${isBodyPartitioned ? 'hidden' : 'auto'};
    }
    .conversation-container {
      .scenario-toggle-button {
        margin-top: 25px;
      }
      height: ${isBodyPartitioned ? 'auto' : '100%'};
      width: ${isBodyPartitioned ? '50%' : '100%'};
      .conversation-body-container {
        min-height: 0;
        flex: 1;
        overflow-y: auto;
        .completion-container {
          margin-top: auto !important;
          .complete-status-container {
            margin-bottom: ${isBodyPartitioned ? standardSpacing : doubleSpacing}px;
          }
        }
      }
      .conversation-footer-container {
        flex-shrink: 0;
      }
      .conversation-body-container, .conversation-footer-container {
        padding-left: ${getResponsivePadding(isBodyPartitioned)}px;
        padding-right: ${getResponsivePadding(isBodyPartitioned)}px;
      }
    }
    .feedback-container {
      width: ${isBodyPartitioned ? '50%' : '100%'};
      border-left: ${isBodyPartitioned ? 1 : 0}px solid ${gray5};
      overflow-y: ${isBodyPartitioned ? 'auto' : 'unset'};
    }
    .scroll-handler {
      bottom: 120px;
      right: ${standardSpacing}px;
      width: ${doubleSpacing}px;
      min-height: ${doubleSpacing}px;
    }
  `;
  const conversationBodyRef = useRef(null);
  const feedbackContainerRef = useRef(null);

  const [isBodyScrollPresent, setIsBodyScrollPresent] = useState(false);
  const [isBodyScrollBarAtBottom, setIsBodyScrollBarAtBottom] = useState(true);

  const getCompletedTimeStamp = () => {
    switch (submission?.closingReason) {
      case ClosingReason.TERMINATED:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.SESSION_ENDED_ON(moment(submission.completedAt).format('LLL'));
      case ClosingReason.COMPLETED:
        return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.COMPLETED_ON(moment(submission.completedAt).format('LLL'));
      default:
        return null;
    }
  };

  useEffect(() => {
    const bodyElement = conversationBodyRef.current;
    let resizeObserver: ResizeObserver | null = null;
    const checkScroll = () => {
      setIsBodyScrollPresent(bodyElement.scrollHeight > bodyElement.clientHeight);
      const isAtBottom = Math.abs(bodyElement.scrollHeight - bodyElement.scrollTop - bodyElement.clientHeight) < 1;
      setIsBodyScrollBarAtBottom(isAtBottom);
    };

    if (bodyElement) {
      bodyElement.addEventListener('scroll', checkScroll);
      // ResizeObserver to detect content/height changes and keep scroll state in sync.
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(() => {
          checkScroll();
        });
        resizeObserver.observe(bodyElement);
      }
    }

    return () => {
      if (bodyElement) {
        bodyElement.removeEventListener('scroll', checkScroll);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
      }
    };
  }, []);

  const lastMessageStatus = messages[messages.length - 1]?.status;
  const totalMessages = messages.length;

  // Auto-scroll to bottom when a new message is added or when the last message's status changes (e.g. from pending to sent).
  useEffect(() => {
    const bodyElement = conversationBodyRef.current;
    const timeoutId = setTimeout(() => {
      // Guard in case the element has been detached or does not support scrollTo.
      if (bodyElement && typeof bodyElement.scrollTo === 'function') {
        bodyElement.scrollTo({
          top: bodyElement.scrollHeight,
          behavior: 'smooth',
        });
      }
    });
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    lastMessageStatus,
    totalMessages,
    submission?.completedAt,
  ]);

  // Scroll to feedback container when feedback generation starts
  // Skip when isBodyPartitioned since feedback is already visible in side-by-side layout
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (!isBodyPartitioned
        && aiRolePlayStatus === AIRolePlayStatus.COMPLETED
        && submission?.closingReason === ClosingReason.COMPLETED) {
      timeoutId = setTimeout(() => {
        if (feedbackContainerRef?.current
            && typeof feedbackContainerRef.current.scrollIntoView === 'function') {
          feedbackContainerRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    submission?.feedback,
    isBodyPartitioned,
    aiRolePlayStatus,
    submission?.closingReason,
  ]);

  const handleToggleScenarioDetails = () => {
    setShowScenarioDetails(!showScenarioDetails);
  };

  const handleScroll = () => {
    if (conversationBodyRef.current) {
      if (!isBodyScrollBarAtBottom) {
        conversationBodyRef.current.scrollTo({
          top: conversationBodyRef.current.scrollHeight,
          behavior: 'smooth',
        });
        return;
      }
      conversationBodyRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div css={styles} className='modal-page position-relative d-flex flex-column'>
      <ConversationModalHeader />
      <div className='body-container h-100 d-flex border-top'>
        <div className='position-relative conversation-container d-flex flex-column'>
          <div ref={conversationBodyRef} className='pt-5 conversation-body-container d-flex flex-column'>
            <AvatarContainer />
            {showScenarioDetails && (
              <ScenarioDetails />
            )}
            {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
            <ClickableContainer
              className='mb-0 text-primary d-flex justify-content-center scenario-toggle-button text-small semi-bold'
              onClick={handleToggleScenarioDetails}
              data-qa={config.pendo.activities.aiRolePlay.conversationModal.toggleScenario}
            >
              <NvIcon
                icon='gen-ai'
                size='xss-smallest'
                className='d-flex align-items-center'
              />
              <p className='mb-0 ml-1'>
                {showScenarioDetails
                  ? t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.HIDE_SCENARIO()
                  : t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.SHOW_SCENARIO()}
              </p>
            </ClickableContainer>
            )}
            {aiRolePlayStatus === AIRolePlayStatus.NOT_STARTED && (
            <div className='d-flex justify-content-center mt-5'>
              <Button
                onClick={handleStartConversation}
                disabled={isRolePlayStarting}
                data-qa={config.pendo.activities.aiRolePlay.conversationModal.begin}
              >
                {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.BEGIN()}
              </Button>
            </div>
            )}
            {(aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS || aiRolePlayStatus === AIRolePlayStatus.COMPLETED)
            && (
              <Chats />
            )}
            {aiRolePlayStatus === AIRolePlayStatus.COMPLETED && (
              <div className='d-flex flex-column completion-container'>
                {submission?.completedAt
                  && (<p className='mt-5 font-weight-normal'>{getCompletedTimeStamp()}</p>)}
                <div className='complete-status-container mt-auto pt-4'>
                  <CompleteStatus />
                </div>
              </div>
            )}
          </div>
          {isBodyScrollPresent && (
            <ClickableContainer
              className='d-flex bg-gray-2 position-absolute scroll-handler rounded-circle mt-auto ml-auto'
              onClick={handleScroll}
              data-qa={config.pendo.activities.aiRolePlay.conversationModal.scrollHandler}
            >
              <NvIcon
                className='text-white d-flex align-items-center m-auto'
                icon={isBodyScrollBarAtBottom ? 'sorting-up-active' : 'sorting-down-active'}
                size='xss-smallest'
              />
            </ClickableContainer>
          )}
          <div className='w-100 conversation-footer-container'>
            {aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS && (
              <ChatBar />
            )}
          </div>
        </div>
        {
          aiRolePlayStatus === AIRolePlayStatus.COMPLETED
          && submission?.closingReason === ClosingReason.COMPLETED
          && (
            <div ref={feedbackContainerRef} className='feedback-container'>
              <AiFeedback
                submissionId={sessionData.submissionId}
              />
            </div>
          )
        }
      </div>
    </div>
  );
};

export default ConversationModalContent;
